import React, { useState } from 'react';
import { motion } from 'framer-motion';

interface PromotionBannerProps {
  onQuoteClick: () => void;
}

const PromotionBanner: React.FC<PromotionBannerProps> = ({ onQuoteClick }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleClick = () => {
    if (!isDragging) {
      onQuoteClick();
    }
  };

  return (
    <motion.div
      drag
      dragMomentum={false}
      dragElastic={0.1}
      onDragStart={() => setIsDragging(true)}
      onDragEnd={() => setIsDragging(false)}
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6, delay: 1 }}
      className={`fixed bottom-6 left-6 z-40 ${
        isDragging ? 'cursor-grabbing' : 'cursor-pointer'
      }`}
      whileHover={{ scale: 1.05 }}
      whileDrag={{ scale: 1.1, rotate: 5 }}
      onHoverStart={() => setIsExpanded(true)}
      onHoverEnd={() => setIsExpanded(false)}
      onClick={handleClick}
    >
      <motion.div 
        className="relative"
        animate={{ 
          width: isExpanded ? 'auto' : '60px',
          height: isExpanded ? 'auto' : '60px'
        }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
      >
        {/* Compact version */}
        <motion.div
          className="bg-gradient-to-br from-coffee-100 to-coffee-200 text-cream-50 rounded-full shadow-card flex items-center justify-center relative overflow-hidden"
          style={{ 
            width: isExpanded ? 'auto' : '60px',
            height: isExpanded ? 'auto' : '60px',
            minWidth: '60px',
            minHeight: '60px'
          }}
          animate={{ 
            padding: isExpanded ? '12px 16px' : '0px'
          }}
        >
          {/* Sparkle animation */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
            animate={{ x: ['-100%', '100%'] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          />
          
          {!isExpanded ? (
            // Compact: Just the percentage
            <div className="text-center">
              <div className="text-lg font-bold">15%</div>
              <div className="text-xs -mt-1">OFF</div>
            </div>
          ) : (
            // Expanded: Full message
            <div className="flex items-center space-x-3 whitespace-nowrap">
              <div className="text-center">
                <div className="text-lg font-bold">15%</div>
                <div className="text-xs -mt-1">OFF</div>
              </div>
              <div className="border-l border-white/30 pl-3">
                <div className="text-sm font-medium">Réduction</div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onQuoteClick();
                  }}
                  className="bg-white/20 hover:bg-white/30 text-white px-3 py-1 rounded-full text-xs font-medium transition-all duration-300 border border-white/30 mt-1"
                >
                  Profiter
                </motion.button>
              </div>
            </div>
          )}
        </motion.div>

        {/* Close button - only visible on hover */}
        <motion.button
          initial={{ opacity: 0, scale: 0 }}
          animate={{ 
            opacity: isExpanded ? 1 : 0,
            scale: isExpanded ? 1 : 0
          }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={(e) => {
            e.stopPropagation();
            e.currentTarget.closest('.fixed')?.remove();
          }}
          className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs transition-colors"
        >
          ×
        </motion.button>

        {/* Drag indicator dots */}
        <motion.div 
          className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 flex space-x-1"
          animate={{ opacity: isDragging ? 1 : 0.3 }}
        >
          <div className="w-1 h-1 bg-white/60 rounded-full"></div>
          <div className="w-1 h-1 bg-white/60 rounded-full"></div>
          <div className="w-1 h-1 bg-white/60 rounded-full"></div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default PromotionBanner;